/** The one scope an activity view is about: a partner, an API key or a tenant. The summary
 *  and the embedded grid take exactly one; this maps it to the API's authoritative scope
 *  param and to the equivalent /external-access link (`partner=#id`, `credential=id`, or
 *  the grid's tenant picker). */
import type { ExternalAccessScope } from '@/api/externalAccess';
import type { FilterNode } from '@/components/QuerySearch';
import { cond, idCond } from '@/utils/gridLinks';

export type ExternalAccessScopeProps =
  | { partnerId: number; credentialId?: never; tenantId?: never }
  | { credentialId: number; partnerId?: never; tenantId?: never }
  | { tenantId: number; partnerId?: never; credentialId?: never };

export type ScopeKind = 'partner' | 'credential' | 'tenant';

export interface ResolvedScope {
  kind: ScopeKind;
  id: number;
  /** The list / summary query params. */
  params: ExternalAccessScope;
  /** The grid conditions naming the scope (none for a tenant: it goes through the picker). */
  nodes: FilterNode[];
  /** The grid's tenant picker value, for a tenant scope. */
  tenant: number | null;
}

export function resolveScope(props: ExternalAccessScopeProps): ResolvedScope {
  if (props.partnerId != null) {
    return {
      kind: 'partner',
      id: props.partnerId,
      params: { partner_id: props.partnerId },
      nodes: [idCond('partner', props.partnerId)],
      tenant: null,
    };
  }
  if (props.credentialId != null) {
    return {
      kind: 'credential',
      id: props.credentialId,
      params: { credential_id: props.credentialId },
      nodes: [cond('credential', String(props.credentialId), 'exact')],
      tenant: null,
    };
  }
  return {
    kind: 'tenant',
    id: props.tenantId,
    params: { tour_operator_id: props.tenantId },
    nodes: [],
    tenant: props.tenantId,
  };
}
